import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle, X } from 'lucide-react'
import { adminApi, type ErrorLogItem } from '../../services/api'

const severityColors: Record<string, string> = {
  Error: 'bg-red-100 text-red-800',
  Critical: 'bg-red-200 text-red-900',
  Warning: 'bg-yellow-100 text-yellow-800',
}

export default function AdminErrorsPage() {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [severityFilter, setSeverityFilter] = useState<string | undefined>()
  const [resolvedFilter, setResolvedFilter] = useState<boolean | undefined>()
  const [selectedErrorId, setSelectedErrorId] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['error-logs', { page, severity: severityFilter, isResolved: resolvedFilter }],
    queryFn: () =>
      adminApi.getErrors({ page, severity: severityFilter, isResolved: resolvedFilter }).then(r => r.data),
  })

  const { data: stats } = useQuery({
    queryKey: ['error-stats'],
    queryFn: () => adminApi.getErrorStats().then(r => r.data),
  })

  const { data: errorDetail, isLoading: detailLoading } = useQuery({
    queryKey: ['error-detail', selectedErrorId],
    queryFn: () => adminApi.getErrorDetail(selectedErrorId!).then(r => r.data),
    enabled: !!selectedErrorId,
  })

  const resolveMutation = useMutation({
    mutationFn: (id: string) => adminApi.resolveError(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['error-logs'] })
      queryClient.invalidateQueries({ queryKey: ['error-stats'] })
      queryClient.invalidateQueries({ queryKey: ['error-detail', selectedErrorId] })
    },
  })

  const last24h = stats?.errorsByDay
    ? Object.entries(stats.errorsByDay)
        .filter(([date]) => {
          const d = new Date(date)
          return d >= new Date(Date.now() - 24 * 60 * 60 * 1000)
        })
        .reduce((sum, [, count]) => sum + count, 0)
    : 0

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Logi błędów</h1>
        <p className="text-gray-500">Błędy aplikacji i nieobsłużone wyjątki</p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <p className="text-sm text-gray-500">Łącznie błędów</p>
          <p className="text-2xl font-bold text-gray-900">{stats?.totalErrors ?? 0}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <p className="text-sm text-gray-500">Nierozwiązane</p>
          <p className="text-2xl font-bold text-red-600">{stats?.unresolvedCount ?? 0}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <p className="text-sm text-gray-500">Ostatnie 24h</p>
          <p className="text-2xl font-bold text-gray-900">{last24h}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setResolvedFilter(undefined)}
          className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
            resolvedFilter === undefined
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Wszystkie
        </button>
        <button
          onClick={() => setResolvedFilter(false)}
          className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
            resolvedFilter === false
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Nierozwiązane
        </button>
        <button
          onClick={() => setResolvedFilter(true)}
          className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
            resolvedFilter === true
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Rozwiązane
        </button>

        <div className="w-px bg-gray-300 mx-1" />

        <button
          onClick={() => setSeverityFilter(undefined)}
          className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
            !severityFilter
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Wszystkie severity
        </button>
        {['Error', 'Critical', 'Warning'].map(s => (
          <button
            key={s}
            onClick={() => setSeverityFilter(s)}
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              severityFilter === s
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {/* Error table */}
      <div className="card">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
          </div>
        ) : data?.items?.length ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Typ</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Komunikat</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Endpoint</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.items.map((error: ErrorLogItem) => (
                    <tr
                      key={error.id}
                      onClick={() => setSelectedErrorId(error.id)}
                      className="hover:bg-gray-50 cursor-pointer"
                    >
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        {new Date(error.createdAt).toLocaleString('pl-PL')}
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-xs">
                        {error.exceptionType.split('.').pop()}
                      </td>
                      <td className="px-4 py-3 text-sm max-w-xs truncate">
                        {error.message}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                        {error.requestMethod && (
                          <span className="font-mono text-xs">
                            {error.requestMethod} {error.requestPath}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${severityColors[error.severity] || 'bg-gray-100 text-gray-700'}`}>
                          {error.severity}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {error.isResolved ? (
                          <span className="text-green-600 flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" /> Rozwiązany
                          </span>
                        ) : (
                          <span className="text-red-600">Otwarty</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <p className="text-sm text-gray-500">
                  Strona {data.page} z {data.totalPages} ({data.totalCount} wyników)
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 text-sm border rounded disabled:opacity-50"
                  >
                    Poprzednia
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(data.totalPages, p + 1))}
                    disabled={page === data.totalPages}
                    className="px-3 py-1 text-sm border rounded disabled:opacity-50"
                  >
                    Następna
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-8 text-center text-gray-500">
            <AlertTriangle className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p>Brak logów błędów</p>
          </div>
        )}
      </div>

      {/* Error detail modal */}
      {selectedErrorId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-bold text-gray-900">Szczegóły błędu</h2>
              <button onClick={() => setSelectedErrorId(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {detailLoading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
              </div>
            ) : errorDetail ? (
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Typ wyjątku</p>
                    <p className="font-mono text-sm">{errorDetail.exceptionType}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Severity</p>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${severityColors[errorDetail.severity] || 'bg-gray-100'}`}>
                      {errorDetail.severity}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Endpoint</p>
                    <p className="font-mono text-sm">{errorDetail.requestMethod} {errorDetail.requestPath}{errorDetail.requestQuery}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Data</p>
                    <p className="text-sm">{new Date(errorDetail.createdAt).toLocaleString('pl-PL')}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">IP</p>
                    <p className="text-sm">{errorDetail.ipAddress || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Status</p>
                    <p className="text-sm">{errorDetail.isResolved ? 'Rozwiązany' : 'Otwarty'}</p>
                  </div>
                </div>

                <div>
                  <p className="text-xs text-gray-500 uppercase mb-1">Komunikat</p>
                  <p className="text-sm bg-gray-50 p-3 rounded">{errorDetail.message}</p>
                </div>

                {errorDetail.innerException && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">Inner Exception</p>
                    <p className="text-sm bg-gray-50 p-3 rounded font-mono">{errorDetail.innerException}</p>
                  </div>
                )}

                {errorDetail.stackTrace && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">Stack Trace</p>
                    <pre className="text-xs bg-gray-900 text-green-400 p-4 rounded overflow-x-auto whitespace-pre-wrap">
                      {errorDetail.stackTrace}
                    </pre>
                  </div>
                )}

                {errorDetail.userAgent && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">User Agent</p>
                    <p className="text-xs text-gray-600">{errorDetail.userAgent}</p>
                  </div>
                )}

                {!errorDetail.isResolved && (
                  <div className="pt-4 border-t">
                    <button
                      onClick={() => resolveMutation.mutate(errorDetail.id)}
                      disabled={resolveMutation.isPending}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 text-sm"
                    >
                      {resolveMutation.isPending ? 'Zapisywanie...' : 'Oznacz jako rozwiązany'}
                    </button>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  )
}
